import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowRight, ArrowLeft, CheckCircle2, Calculator, BookOpen } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Reveal, SectionLabel, PrimaryCTA, ExpertCTA } from "@/components/ui-kit";
import { Icon } from "@/components/Sections";
import { SERVICES } from "@/data";
import { SERVICE_DETAILS } from "@/content/services";
import { getArticle } from "@/content/articles";
import { guideForService } from "@/content/guides";
import { GuideCard } from "@/components/GuideUnlock";
import { CALC_META, calcPath } from "@/components/calculators/meta";
import { usePageMeta } from "@/hooks/usePageMeta";
import { whatsappFor } from "@/config";

export default function ServiceDetailPage() {
  const { id } = useParams();
  const s = SERVICES.find((x) => x.id === id);
  const d = SERVICE_DETAILS[id];

  usePageMeta({
    title: s ? `${s.title} — ${d.eyebrow}` : "Service",
    description: s ? d.intro.slice(0, 155) : "",
    path: `/services/${id}`,
  });

  if (!s || !d) return <Navigate to="/services" replace />;
  const calc = CALC_META[d.calculator];
  const others = SERVICES.filter((x) => x.id !== id).slice(0, 4);

  return (
    <>
      <section className="relative overflow-hidden bg-lightbg pt-28 sm:pt-32 lg:pt-36">
        <div className="pointer-events-none absolute -right-40 -top-24 h-[480px] w-[480px] rounded-full bg-royal/[0.07] blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-5 pb-14 sm:px-8 lg:pb-20">
          <Link to="/services" data-testid="service-back" className="inline-flex items-center gap-2 text-sm font-semibold text-navy/60 hover:text-navy">
            <ArrowLeft className="h-4 w-4" /> All services
          </Link>
          <div className="mt-8 grid items-start gap-10 lg:grid-cols-[1.1fr_0.9fr]">
            <div>
              <div className="flex items-center gap-3">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-navy text-gold">
                  <Icon name={s.icon} className="h-6 w-6" />
                </span>
                <SectionLabel testId="service-detail-label">{d.eyebrow}</SectionLabel>
              </div>
              <h1 data-testid="service-detail-title" className="mt-6 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-navy sm:text-5xl lg:text-6xl">
                {d.headline}
              </h1>
              {s.badge && (
                <span className="mt-4 inline-flex rounded-full bg-gold/15 px-3 py-1 text-xs font-semibold text-navy/80">{s.badge}</span>
              )}
              <p className="mt-6 max-w-2xl text-base leading-relaxed text-ink/70 sm:text-lg">{d.intro}</p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <PrimaryCTA testId="service-detail-primary-cta" />
                <ExpertCTA testId="service-detail-expert-cta" href={whatsappFor(s.title)}>Ask about {s.title}</ExpertCTA>
              </div>
            </div>
            <Reveal delay={0.15}>
              <div className="rounded-3xl border border-navy/10 bg-white p-7 shadow-hover">
                <p className="text-xs font-semibold uppercase tracking-wide text-ink/40">Good for</p>
                <ul className="mt-4 space-y-3">
                  {d.goodFor.map((g) => (
                    <li key={g} className="flex items-start gap-3 text-[15px] text-ink/75">
                      <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-royal" /> {g}
                    </li>
                  ))}
                </ul>
                {calc && (
                  <Link to={calcPath(d.calculator)} data-testid="service-calc-link" className="mt-6 flex items-center justify-between rounded-2xl bg-lightbg px-4 py-3 text-sm font-semibold text-navy transition-colors hover:bg-navy hover:text-white">
                    <span className="flex items-center gap-2"><Calculator className="h-4 w-4 text-gold" /> Try the {calc.title}</span>
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* what's included */}
      <section className="bg-white py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <SectionLabel testId="service-includes-label">What's included</SectionLabel>
            <h2 className="mt-5 max-w-2xl font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
              How we help with {s.title.toLowerCase()}.
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            {d.includes.map(([h, body], i) => (
              <Reveal key={h} delay={(i % 3) * 0.08}>
                <div data-testid={`service-include-${i}`} className="flex h-full flex-col rounded-2xl border border-navy/[0.08] bg-lightbg/60 p-7 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-hover">
                  <span className="font-display text-sm font-bold text-gold">0{i + 1}</span>
                  <h3 className="mt-3 font-display text-lg font-bold text-navy">{h}</h3>
                  <p className="mt-2 text-[15px] leading-relaxed text-ink/65">{body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* faqs + articles */}
      <section className="bg-lightbg py-20 sm:py-28">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-[1.1fr_0.9fr]">
          <Reveal>
            <SectionLabel testId="service-faq-label">Common questions</SectionLabel>
            <Accordion type="single" collapsible className="mt-6 space-y-3" data-testid="service-faq">
              {d.faqs.map(([q, a], i) => (
                <AccordionItem key={q} value={`s-${i}`} className="overflow-hidden rounded-2xl border border-navy/[0.08] bg-white px-5">
                  <AccordionTrigger className="py-5 text-left font-display text-base font-bold text-navy hover:no-underline">{q}</AccordionTrigger>
                  <AccordionContent className="pb-5 text-[15px] leading-relaxed text-ink/65">{a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
            <p className="mt-6 rounded-2xl border border-gold/30 bg-gold/[0.07] p-4 text-xs leading-relaxed text-ink/60">{d.note}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <SectionLabel testId="service-learn-label">Learn more</SectionLabel>
            <div className="mt-6 space-y-4">
              {d.articles.map((slug) => {
                const a = getArticle(slug);
                if (!a) return null;
                return (
                  <Link key={slug} to={`/learn/${slug}`} data-testid={`service-article-${slug}`} className="group flex gap-4 rounded-2xl border border-navy/[0.08] bg-white p-4 transition-[transform,box-shadow] hover:-translate-y-0.5 hover:shadow-hover">
                    <img src={a.cover} alt={a.alt} loading="lazy" className="h-20 w-28 shrink-0 rounded-xl object-cover" />
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-royal">{a.category} · {a.readTime}</p>
                      <h3 className="mt-1 font-display text-base font-bold leading-snug text-navy group-hover:text-royal">{a.title}</h3>
                    </div>
                  </Link>
                );
              })}
              <Link to="/learn" className="inline-flex items-center gap-2 text-sm font-semibold text-royal hover:text-navy">
                <BookOpen className="h-4 w-4" /> Browse the Learn hub <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="mt-8"><GuideCard guide={guideForService(id)} compact /></div>
            <p className="mt-10 text-xs font-semibold uppercase tracking-wide text-ink/40">Other services</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {others.map((o) => (
                <Link key={o.id} to={`/services/${o.id}`} className="rounded-full border border-navy/10 bg-white px-4 py-2 text-sm font-medium text-navy/80 transition-colors hover:border-royal hover:text-royal">
                  {o.title}
                </Link>
              ))}
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
